import {
  IamGroupType,
  type UserGroupExportExcelApiData,
  type UserGroupFilterCountsApiData,
  type UserGroupListApiData
} from '@navio/nsdk';
import { filterUnitFor, type SharedQuery, useFilterSpecValue, useFilterUnits } from 'noddi-ui-web';
import { CUSTOMER_SEGMENT_OPTIONS, PHONE_NUMBER_COUNTRY_OPTIONS } from 'noddi-util';
import { useAccountOptions } from '../Account/useAccountFilter';
import { ADDRESS_FILTER_TEXT, useAddressOptions } from '../Address/useAddressFilter';
import { PAYMENT_OPTION_FILTER_OPTIONS } from '../Booking/usePaymentOptionsFilter';
import { CREATED_AT_FILTER_TEXT } from '../Date/useCreatedAtFilter';
import { UPDATED_AT_FILTER_TEXT } from '../Date/useUpdatedAtFilter';
import { useIamRoleOptions } from '../IamRole/useIamRoleOptions';
import { CITY_FILTER_TEXT, useCityOptions } from '../Location/useCitiesFilter';
import { ORGANIZATION_FILTER_TEXT, useOrganizationOptions } from '../Organization/useOrganizationFilter';
import { ORGANIZATION_TYPE_FILTER_OPTIONS } from '../Organization/useOrganizationTypeFilter';
import { CUSTOMER_GROUP_TYPE_FILTER_OPTIONS } from './useCustomerGroupTypeFilter';
import { PHONE_NUMBER_TYPE_FILTER_OPTIONS } from './usePhoneNumberTypeFilter';
import { COHORT_FILTER_TEXT, useUserGroupCohortOptions } from './useUserGroupCohortFilter';
import { DATA_QUALITY_ISSUE_TYPES_FILTER, HAS_DATA_QUALITY_ISSUES_FILTER_TEXT } from './useUserGroupDataQualityFilters';
import { useUserGroupOptions } from './useUserGroupFilter';

type ListOnly = 'pageIndex' | 'pageSize' | 'ordering' | 'search';

/** Params the user group list, its facet counts and its Excel export all take: one filtered set for the three. */
export type UserGroupFilterQuery = Omit<
  SharedQuery<
    NonNullable<UserGroupListApiData['query']>,
    SharedQuery<NonNullable<UserGroupFilterCountsApiData['query']>, NonNullable<UserGroupExportExcelApiData['query']>>
  >,
  ListOnly
>;

export const userGroupFilterUnit = filterUnitFor<UserGroupFilterQuery>();

/**
 * Text and static options of the customer filters, by param. Lists on other endpoints that offer the same
 * filters (adapter lists) build their own builders from these, on the same URL keys.
 */
export const USER_GROUP_FILTER_TEXT = {
  types: {
    title: 'Customer Group Types',
    description: 'Filter by customer group type',
    options: CUSTOMER_GROUP_TYPE_FILTER_OPTIONS
  },
  organizationIds: ORGANIZATION_FILTER_TEXT,
  organizationTypes: {
    title: 'Organization type',
    description: 'Show customers whose organization is one of these types.',
    options: ORGANIZATION_TYPE_FILTER_OPTIONS
  },
  ids: { title: 'Groups', description: 'Filter by specific groups', defaultVisibleOptions: 3 },
  carManagerIds: { title: 'Driver', description: 'Filter by people who are a driver or car manager for the car.' },
  userGroupPayerIds: {
    title: 'Invoice to',
    description: 'Filter by the organization the customer is invoiced to (Faktureres til)',
    defaultVisibleOptions: 3
  },
  segments: {
    title: 'Customer segment',
    description:
      'Lifecycle and loyalty segments: prospects (no bookings), new (1 completed), active clients (2-9), VIP (10+ or high spend), at risk / churned by recency, and fleet (3+ vehicles). All includes everyone.',
    options: CUSTOMER_SEGMENT_OPTIONS
  },
  paymentOptions: {
    title: 'Payment option',
    description: 'How the customer pays for the booking (e.g. pay before, pay after, invoice).',
    options: PAYMENT_OPTION_FILTER_OPTIONS
  },
  phoneNumberTypes: {
    title: 'Phone number type',
    description: 'Filter by phone number type (e.g. mobile, landline).',
    options: PHONE_NUMBER_TYPE_FILTER_OPTIONS
  },
  phoneNumberCountries: {
    title: 'Phone number country',
    description: 'Filter by the country of the phone number.',
    options: PHONE_NUMBER_COUNTRY_OPTIONS
  },
  cities: CITY_FILTER_TEXT,
  hasCars: { title: 'Has cars', description: 'Specifies if the group has cars.' },
  hasTireInTireHotel: {
    title: 'Has tire in tire hotel',
    description: 'Show groups with at least one car that currently has tires stored in a tire hotel.'
  },
  hasDuplicatePhoneNumber: {
    title: 'Duplicate phone number',
    description: 'Show customers that share a phone number with another account.'
  },
  numCars: {
    title: 'Number of cars',
    description: 'Filter by number of cars in the group.',
    minLabel: 'Min cars',
    maxLabel: 'Max cars'
  }
} as const;

/**
 * The customer list filters (admin customers, partner customers). `commonFilterUnit` is what every app shows;
 * `adminFilterUnit` adds the admin-only ones. Both
 * are bound to `UserGroupFilterQuery`, so their `apiInput` goes to the list, the counts and the export alike.
 */
export const useUserGroupTableFilters = (
  serviceDepartmentIds?: number[],
  { includeAdminFilters = true }: { includeAdminFilters?: boolean } = {}
) => {
  const text = USER_GROUP_FILTER_TEXT;
  const citiesFilter = userGroupFilterUnit.multiSelect('cities', {
    ...text.cities,
    source: useCityOptions(),
    exclude: true
  });
  const { cities } = useFilterSpecValue(citiesFilter);
  // Unscoped organizations (same as recalls), so the partner customer list can filter on them too.
  const organizations = useOrganizationOptions();
  const groups = useUserGroupOptions({ types: [IamGroupType.GROUP] });
  // Car managers are users: options are user ids.
  const drivers = useAccountOptions();
  const payers = useUserGroupOptions({ types: [IamGroupType.ORGANIZATION], isInvoicePayer: true });
  const addresses = useAddressOptions({ serviceDepartmentIds, cities, enabled: includeAdminFilters });
  const cohorts = useUserGroupCohortOptions({ enabled: includeAdminFilters });
  const iamRoles = useIamRoleOptions({ enabled: includeAdminFilters });

  const commonFilterUnit = useFilterUnits([
    userGroupFilterUnit.multiSelect('organizationIds', {
      ...text.organizationIds,
      source: organizations,
      exclude: true
    }),
    userGroupFilterUnit.multiSelect('organizationTypes', { ...text.organizationTypes, exclude: true }),
    userGroupFilterUnit.multiSelect('ids', { ...text.ids, source: groups, exclude: true }),
    userGroupFilterUnit.multiSelect('carManagerIds', { ...text.carManagerIds, source: drivers, exclude: true }),
    userGroupFilterUnit.multiSelect('userGroupPayerIds', { ...text.userGroupPayerIds, source: payers, exclude: true }),
    userGroupFilterUnit.multiSelect('segments', { ...text.segments, exclude: true }),
    userGroupFilterUnit.multiSelect('paymentOptions', { ...text.paymentOptions, exclude: true }),
    userGroupFilterUnit.multiSelect('phoneNumberTypes', { ...text.phoneNumberTypes, exclude: true }),
    userGroupFilterUnit.multiSelect('phoneNumberCountries', { ...text.phoneNumberCountries, exclude: true }),
    citiesFilter,
    userGroupFilterUnit.boolean('hasCars', text.hasCars),
    userGroupFilterUnit.boolean('hasTireInTireHotel', text.hasTireInTireHotel),
    userGroupFilterUnit.boolean('hasInvoice', {
      title: 'Has invoice',
      description: 'Show groups allowed to pay with invoice (Faktura).'
    }),
    userGroupFilterUnit.boolean('hasDuplicatePhoneNumber', text.hasDuplicatePhoneNumber),
    userGroupFilterUnit.boolean('missingAdmin', {
      title: 'Missing admin',
      description: 'Show organizations and groups with active members but no active admin.'
    }),
    userGroupFilterUnit.boolean('invalidEmail', {
      title: 'Missing or invalid email',
      description: 'Filter customers with no usable email address.'
    }),
    userGroupFilterUnit.boolean('emailVerified', {
      title: 'Email verified',
      description: 'Show customers whose primary contact email is verified.'
    }),
    userGroupFilterUnit.boolean('phoneNumberVerified', {
      title: 'Phone verified',
      description: 'Show customers whose primary contact phone number is verified.'
    }),
    userGroupFilterUnit.dateRange('phoneChangedAt', {
      title: 'Phone changed',
      description: 'Phone number changed in this date range.',
      exclude: true
    }),
    userGroupFilterUnit.dateRange('emailChangedAt', {
      title: 'Email changed',
      description: 'Email changed in this date range.',
      exclude: true
    }),
    userGroupFilterUnit.dateRange('nameChangedAt', {
      title: 'Name changed',
      description: 'First or last name, or the group name, changed in this date range.',
      exclude: true
    }),
    userGroupFilterUnit.boolean('hasDataQualityIssues', HAS_DATA_QUALITY_ISSUES_FILTER_TEXT),
    userGroupFilterUnit.multiSelect('dataQualityIssueTypes', { ...DATA_QUALITY_ISSUE_TYPES_FILTER, exclude: true }),
    userGroupFilterUnit.numberRange('numCars', { ...text.numCars, exclude: true }),
    userGroupFilterUnit.dateRange('createdAt', { ...CREATED_AT_FILTER_TEXT, exclude: true }),
    userGroupFilterUnit.dateRange('updatedAt', { ...UPDATED_AT_FILTER_TEXT, exclude: true })
  ]);

  const adminFilterUnit = useFilterUnits([
    userGroupFilterUnit.multiSelect('addressIds', { ...ADDRESS_FILTER_TEXT, source: addresses, exclude: true }),
    userGroupFilterUnit.boolean('emailBookingNotifications', {
      title: 'Email booking notifications',
      description: 'Specifies if the group has email booking notifications.'
    }),
    userGroupFilterUnit.boolean('smsBookingNotifications', {
      title: 'SMS booking notifications',
      description: 'Specifies if the group has SMS booking notifications.'
    }),
    userGroupFilterUnit.boolean('emailCalendarInvites', {
      title: 'Email calendar invites',
      description: 'Specifies if the group has email calendar invites.'
    }),
    userGroupFilterUnit.multiSelect('cohorts', { ...COHORT_FILTER_TEXT, source: cohorts, exclude: true }),
    // Staff only: the endpoint answers 403 to anyone else.
    userGroupFilterUnit.multiSelect('iamRoleIds', {
      title: 'IAM roles',
      description: 'Groups granted any of these IAM roles, at any scope.',
      source: iamRoles,
      exclude: true
    })
  ]);

  return { commonFilterUnit, adminFilterUnit };
};
